import { Capacitor } from '@capacitor/core';
import { APP_NAME, BASE_URL, PAGE_APP_VERSION, PAGE_ID } from '@config';
import { encryptString, decryptString, shouldBypassEncryption } from './crypto';

function resolveBackendUrl(): string {
  const override = import.meta.env.VITE_BACKEND_URL?.trim();
  if (override) return override;
  if (import.meta.env.DEV && Capacitor.getPlatform() === 'web') return '';
  return BASE_URL;
}

export const RESOLVED_BACKEND_URL: string = resolveBackendUrl();
export const CRYPTO_SIGNAL_API_BASE = '/crypto-signal';

export function cryptoSignalApi(subpath: string): string {
  const clean = subpath.startsWith('/') ? subpath : `/${subpath}`;
  return `${CRYPTO_SIGNAL_API_BASE}${clean}`;
}

export interface ApiContext {
  ProfileEmail?: string | null;
  FirebaseToken?: string | null;
  JwtToken?: string | null;
}

let context: ApiContext = {};

export function setApiContext(next: ApiContext): void {
  context = { ...context, ...next };
}

export function clearApiContext(): void {
  context = {};
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function buildHeaders(extra: Record<string, string> = {}): Headers {
  const headers = new Headers({
    'Content-Type': 'application/json',
    'X-Page-Id': String(PAGE_ID),
    'X-Page-App-Version': String(PAGE_APP_VERSION),
    'X-Platform': Capacitor.getPlatform(),
    'X-App-Name': APP_NAME,
    ...extra,
  });
  if (context.ProfileEmail) headers.set('X-Profile-Email', context.ProfileEmail);
  if (context.FirebaseToken) headers.set('X-Firebase-Token', context.FirebaseToken);
  if (context.JwtToken) headers.set('Authorization', `Bearer ${context.JwtToken}`);
  return headers;
}

interface PostOptions {
  signal?: AbortSignal;
  encrypted?: boolean;
}

function extractErrorMessage(
  detail: unknown,
  parsed: unknown,
  rawText: string,
  path: string,
  status: number,
): string {
  const fromObject = (value: unknown): string | null => {
    if (!value || typeof value !== 'object') return null;
    const rec = value as Record<string, unknown>;
    const found = rec.Error ?? rec.error ?? rec.message ?? rec.Message;
    if (typeof found === 'string' && found.trim().length > 0) return found.trim();
    return null;
  };
  const fromDetail = fromObject(detail) ?? fromObject(parsed);
  if (fromDetail) return fromDetail;
  const snippet = (rawText || '').trim().slice(0, 200);
  if (snippet) return `POST ${path} failed (${status}): ${snippet}`;
  return `POST ${path} failed (${status})`;
}

export async function apiPost<TResponse, TBody = unknown>(
  path: string,
  body: TBody,
  options: PostOptions = {},
): Promise<TResponse> {
  const url = `${RESOLVED_BACKEND_URL}${path}`;
  const encrypted = (options.encrypted ?? true) && !shouldBypassEncryption();
  const payload = encrypted ? encryptString(JSON.stringify(body)) : JSON.stringify(body);

  const res = await fetch(url, {
    method: 'POST',
    headers: buildHeaders(
      encrypted ? { 'X-Encrypted': '1' } : { 'Content-Type': 'application/json' },
    ),
    body: encrypted ? JSON.stringify({ Payload: payload }) : payload,
    signal: options.signal,
  });

  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = text;
  }

  if (!res.ok) {
    let detail: unknown = parsed;
    if (
      encrypted &&
      parsed &&
      typeof parsed === 'object' &&
      'Payload' in (parsed as Record<string, unknown>)
    ) {
      try {
        const decoded = decryptString((parsed as { Payload: string }).Payload);
        try {
          detail = JSON.parse(decoded);
        } catch {
          detail = decoded;
        }
      } catch {
        /* keep */
      }
    }
    throw new ApiError(extractErrorMessage(detail, parsed, text, path, res.status), res.status, detail);
  }

  if (
    encrypted &&
    parsed &&
    typeof parsed === 'object' &&
    'Payload' in (parsed as Record<string, unknown>)
  ) {
    const decrypted = decryptString((parsed as { Payload: string }).Payload);
    try {
      return JSON.parse(decrypted) as TResponse;
    } catch {
      return decrypted as unknown as TResponse;
    }
  }

  return parsed as TResponse;
}

export async function apiPostForm<TResponse>(
  path: string,
  formData: FormData,
  options: { signal?: AbortSignal } = {},
): Promise<TResponse> {
  const url = `${RESOLVED_BACKEND_URL}${path}`;
  const headers = buildHeaders();
  headers.delete('Content-Type');
  const res = await fetch(url, { method: 'POST', headers, body: formData, signal: options.signal });
  const text = await res.text();
  let parsed: unknown = null;
  try {
    parsed = JSON.parse(text);
  } catch {
    parsed = text;
  }
  if (!res.ok) {
    throw new ApiError(extractErrorMessage(parsed, parsed, text, path, res.status), res.status, parsed);
  }
  return parsed as TResponse;
}
